import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { getCountryOptions } from '../lib/countries';
import {
  getFieldSection,
  getMaxLength,
  isFieldRequired,
  mapBackendErrors,
  validateField,
  validateForm,
  validateSection
} from '../lib/accountValidation';
import { 
  Building2, CreditCard, Users, MapPin, ArrowLeft, Send, 
  Loader2, CheckCircle, AlertCircle, Plus, Trash2, Globe2, ChevronDown
} from 'lucide-react';

const FieldError = ({ name, message }) =>
  message ? (
    <p id={`${name}-error`} role="alert" className="text-xs text-red-400" data-testid={`error-${name}`}>
      {message}
    </p>
  ) : null;

// InputField component moved outside to prevent re-creation on each render
const InputField = ({ label, name, value, onChange, onBlur, type = 'text', required = false, placeholder = '', maxLength, error }) => {
  return (
    <div className="space-y-2">
      <label htmlFor={name} className="block text-xs font-bold uppercase tracking-wider text-gray-400">
        {label} {required && <span className="text-[#7AC142]">*</span>}
      </label>
      <input
        id={name}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        required={required}
        maxLength={maxLength}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${name}-error` : undefined}
        className={`w-full bg-white/5 border text-white px-4 py-3 focus:border-[#7AC142] focus:outline-none focus:bg-white/10 transition-all placeholder-gray-600 ${
          error ? 'border-red-500 bg-red-500/10' : 'border-white/10'
        }`}
        data-testid={`input-${name}`}
      />
      <FieldError name={name} message={error} />
    </div>
  );
};

// SelectField component for country dropdown. Each option is { value, label }:
// the value sent to the backend does not depend on the language.
const SelectField = ({ label, name, value, onChange, onBlur, options, required = false, error, placeholder = 'Select...' }) => {
  return (
    <div className="space-y-2">
      <label htmlFor={name} className="block text-xs font-bold uppercase tracking-wider text-gray-400">
        {label} {required && <span className="text-[#7AC142]">*</span>}
      </label>
      <div className="relative">
        <select
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          required={required}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${name}-error` : undefined}
          className={`w-full bg-white/5 border text-white px-4 py-3 focus:border-[#7AC142] focus:outline-none focus:bg-white/10 transition-all appearance-none cursor-pointer ${
            error ? 'border-red-500 bg-red-500/10' : 'border-white/10'
          } ${!value ? 'text-gray-500' : ''}`}
          data-testid={`select-${name}`}
        >
          <option value="" className="bg-[#1a1a1a]">{placeholder}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value} className="bg-[#1a1a1a]">
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
      </div>
      <FieldError name={name} message={error} />
    </div>
  );
};

const trimValues = (values) =>
  Object.fromEntries(Object.entries(values).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value]));

const trimFormData = (formData) => ({
  ...trimValues(formData),
  shippingAddresses: formData.shippingAddresses.map(trimValues)
});

const FORM_BUTTON = 'inline-flex items-center justify-center gap-2 px-8 py-4 font-bold uppercase tracking-wider transition-colors';

const AccountSetupPage = () => {
  const { language, t, changeLanguage } = useLanguage();
  const languages = ['EN', 'ES', 'PT', 'ZH'];
  
  const [activeSection, setActiveSection] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  
  const [formData, setFormData] = useState({
    // Client Information
    customerName: '',
    companyName: '',
    taxId: '',
    address: '',
    district: '',
    city: '',
    state: '',
    phoneNumber: '',
    zipCode: '',
    webpage: '',
    
    // Bank Information
    beneficiaryName: '',
    bankName: '',
    accountNumber: '',
    bankAddress: '',
    bankCountry: '',
    currency: '',
    swiftCode: '',
    abaCode: '',
    
    // Correspondent Bank
    corrBankName: '',
    corrAccountNumber: '',
    corrBankAddress: '',
    corrCountry: '',
    corrCurrency: '',
    corrSwiftCode: '',
    corrAbaCode: '',
    
    // Payoneer
    usePayoneer: false,
    payoneerAccount: '',
    payoneerCurrency: '',
    
    // Contacts
    commercialName: '',
    commercialPosition: '',
    commercialPhone: '',
    commercialEmail: '',
    commercialWechat: '',
    commercialSkype: '',
    
    operationsName: '',
    operationsPosition: '',
    operationsPhone: '',
    operationsEmail: '',
    operationsWechat: '',
    operationsSkype: '',
    
    financialName: '',
    financialPosition: '',
    financialPhone: '',
    financialCellPhone: '',
    financialEmail: '',
    financialWechat: '',
    financialSkype: '',
    
    // Shipping Addresses
    shippingAddresses: [
      {
        warehouseLegalName: '',
        warehouseManager: '',
        warehousePhone: '',
        shippingAddress: '',
        city: '',
        zipCode: '',
        state: '',
        country: ''
      }
    ]
  });

  const sections = [
    { id: 'client', icon: Building2, label: t('accountSetup.sections.clientInfo') },
    { id: 'bank', icon: CreditCard, label: t('accountSetup.sections.bankInfo') },
    { id: 'contacts', icon: Users, label: t('accountSetup.sections.contacts') },
    { id: 'shipping', icon: MapPin, label: t('accountSetup.sections.shippingAddresses') }
  ];

  const [fieldErrors, setFieldErrors] = useState({});
  const [pendingFocus, setPendingFocus] = useState(null);
  const formTopRef = useRef(null);
  const countryOptions = useMemo(() => getCountryOptions(language), [language]);

  // Fields only exist in the DOM once their section is rendered
  useEffect(() => {
    if (pendingFocus) {
      document.getElementsByName(pendingFocus)[0]?.focus();
      setPendingFocus(null);
    }
  }, [pendingFocus]);

  const setFieldError = (name, nextFormData) => {
    const code = validateField(name, nextFormData);
    setFieldErrors(prev => {
      if (prev[name] === (code || undefined)) return prev;
      const next = { ...prev };
      if (code) next[name] = code;
      else delete next[name];
      return next;
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextFormData = { ...formData, [name]: type === 'checkbox' ? checked : value };
    setFormData(nextFormData);
    if (fieldErrors[name]) setFieldError(name, nextFormData);
  };

  const handleShippingChange = (index, field, value) => {
    const newAddresses = [...formData.shippingAddresses];
    newAddresses[index] = { ...newAddresses[index], [field]: value };
    const nextFormData = { ...formData, shippingAddresses: newAddresses };
    setFormData(nextFormData);
    // Filling or clearing an address changes which of its fields are mandatory
    setFieldErrors(prev => {
      const next = { ...prev };
      Object.keys(prev)
        .filter(name => name.endsWith(`-${index}`))
        .forEach(name => {
          const code = validateField(name, nextFormData);
          if (code) next[name] = code;
          else delete next[name];
        });
      return next;
    });
  };

  const handleBlur = (e) => setFieldError(e.target.name, formData);

  const errorMessage = (name) => {
    const code = fieldErrors[name];
    if (!code) return undefined;
    if (code === 'required') return t('accountSetup.form.required');
    return t(`accountSetup.form.errors.${code}`).replace('{max}', getMaxLength(name));
  };

  const fieldProps = (name) => ({
    name,
    value: formData[name],
    onChange: handleChange,
    onBlur: handleBlur,
    required: isFieldRequired(name, formData),
    maxLength: getMaxLength(name),
    error: errorMessage(name)
  });

  const shippingFieldProps = (index, field) => {
    const name = `${field}-${index}`;
    return {
      name,
      value: formData.shippingAddresses[index][field],
      onChange: (e) => handleShippingChange(index, field, e.target.value),
      onBlur: handleBlur,
      required: isFieldRequired(name, formData),
      maxLength: getMaxLength(name),
      error: errorMessage(name)
    };
  };

  const showErrors = (errors) => {
    const names = Object.keys(errors);
    const section = Math.min(...names.map(getFieldSection));
    setFieldErrors(prev => ({ ...prev, ...errors }));
    setSubmitStatus('validation');
    setActiveSection(section);
    setPendingFocus(names.find(name => getFieldSection(name) === section));
  };

  // Going back is always allowed; going forward requires every section in between to be valid
  const goToSection = (target) => {
    for (let section = activeSection; section < target; section += 1) {
      const errors = validateSection(section, formData);
      if (Object.keys(errors).length > 0) {
        showErrors(errors);
        return;
      }
    }
    setSubmitStatus(null);
    setActiveSection(target);
    if (target !== activeSection) {
      formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const addShippingAddress = () => {
    if (formData.shippingAddresses.length < 3) {
      setFormData(prev => ({
        ...prev,
        shippingAddresses: [
          ...prev.shippingAddresses,
          {
            warehouseLegalName: '',
            warehouseManager: '',
            warehousePhone: '',
            shippingAddress: '',
            city: '',
            zipCode: '',
            state: '',
            country: ''
          }
        ]
      }));
    }
  };

  const removeShippingAddress = (index) => {
    if (formData.shippingAddresses.length > 1) {
      setFormData(prev => ({
        ...prev,
        shippingAddresses: prev.shippingAddresses.filter((_, i) => i !== index)
      }));
      // Indexes shift, so errors of the remaining addresses are checked again on submit
      setFieldErrors(prev => Object.fromEntries(Object.entries(prev).filter(([name]) => !/-\d+$/.test(name))));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);
    
    // Validate form
    const errors = validateForm(formData);
    setFieldErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      showErrors(errors);
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Send data to backend (which generates PDF and forwards to N8N)
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const data = trimFormData(formData);
      const response = await fetch(`${API_URL}/api/customer-form`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          timestamp: new Date().toISOString(),
          // Client Information
          customerName: data.customerName,
          companyName: data.companyName,
          taxId: data.taxId,
          address: data.address,
          district: data.district,
          city: data.city,
          state: data.state,
          phoneNumber: data.phoneNumber,
          zipCode: data.zipCode,
          webpage: data.webpage,
          
          // Bank Information
          beneficiaryName: data.beneficiaryName,
          bankName: data.bankName,
          accountNumber: data.accountNumber,
          bankAddress: data.bankAddress,
          bankCountry: data.bankCountry,
          currency: data.currency,
          swiftCode: data.swiftCode,
          abaCode: data.abaCode,
          
          // Correspondent Bank
          corrBankName: data.corrBankName,
          corrAccountNumber: data.corrAccountNumber,
          corrBankAddress: data.corrBankAddress,
          corrCountry: data.corrCountry,
          corrCurrency: data.corrCurrency,
          corrSwiftCode: data.corrSwiftCode,
          corrAbaCode: data.corrAbaCode,
          
          // Payoneer
          usePayoneer: data.usePayoneer,
          payoneerAccount: data.usePayoneer ? data.payoneerAccount : '',
          payoneerCurrency: data.usePayoneer ? data.payoneerCurrency : '',
          
          // Commercial Contact
          commercialName: data.commercialName,
          commercialPosition: data.commercialPosition,
          commercialPhone: data.commercialPhone,
          commercialEmail: data.commercialEmail,
          commercialWechat: data.commercialWechat,
          commercialSkype: data.commercialSkype,
          
          // Operations Contact
          operationsName: data.operationsName,
          operationsPosition: data.operationsPosition,
          operationsPhone: data.operationsPhone,
          operationsEmail: data.operationsEmail,
          operationsWechat: data.operationsWechat,
          operationsSkype: data.operationsSkype,
          
          // Financial Contact
          financialName: data.financialName,
          financialPosition: data.financialPosition,
          financialPhone: data.financialPhone,
          financialCellPhone: data.financialCellPhone,
          financialEmail: data.financialEmail,
          financialWechat: data.financialWechat,
          financialSkype: data.financialSkype,
          
          // Shipping Addresses (flattened for Google Sheets)
          shippingAddress1_legalName: data.shippingAddresses[0]?.warehouseLegalName || '',
          shippingAddress1_manager: data.shippingAddresses[0]?.warehouseManager || '',
          shippingAddress1_phone: data.shippingAddresses[0]?.warehousePhone || '',
          shippingAddress1_address: data.shippingAddresses[0]?.shippingAddress || '',
          shippingAddress1_city: data.shippingAddresses[0]?.city || '',
          shippingAddress1_zipCode: data.shippingAddresses[0]?.zipCode || '',
          shippingAddress1_state: data.shippingAddresses[0]?.state || '',
          shippingAddress1_country: data.shippingAddresses[0]?.country || '',
          
          shippingAddress2_legalName: data.shippingAddresses[1]?.warehouseLegalName || '',
          shippingAddress2_manager: data.shippingAddresses[1]?.warehouseManager || '',
          shippingAddress2_phone: data.shippingAddresses[1]?.warehousePhone || '',
          shippingAddress2_address: data.shippingAddresses[1]?.shippingAddress || '',
          shippingAddress2_city: data.shippingAddresses[1]?.city || '',
          shippingAddress2_zipCode: data.shippingAddresses[1]?.zipCode || '',
          shippingAddress2_state: data.shippingAddresses[1]?.state || '',
          shippingAddress2_country: data.shippingAddresses[1]?.country || '',
          
          shippingAddress3_legalName: data.shippingAddresses[2]?.warehouseLegalName || '',
          shippingAddress3_manager: data.shippingAddresses[2]?.warehouseManager || '',
          shippingAddress3_phone: data.shippingAddresses[2]?.warehousePhone || '',
          shippingAddress3_address: data.shippingAddresses[2]?.shippingAddress || '',
          shippingAddress3_city: data.shippingAddresses[2]?.city || '',
          shippingAddress3_zipCode: data.shippingAddresses[2]?.zipCode || '',
          shippingAddress3_state: data.shippingAddresses[2]?.state || '',
          shippingAddress3_country: data.shippingAddresses[2]?.country || '',
        }),
      });

      const result = await response.json();
      
      if (response.status === 422) {
        const backendErrors = mapBackendErrors(result.detail);
        if (Object.keys(backendErrors).length > 0) {
          showErrors(backendErrors);
          return;
        }
      }

      if (result.success) {
        setSubmitStatus('success');
        setFieldErrors({});
      } else {
        throw new Error(result.message || 'Error submitting form');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="account-setup-page">
      {/* Header */}
      <header className="bg-black border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-6">
              <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
                <img
                  src="/logo.png"
                  alt="MailAmericas"
                  className="h-7 sm:h-10 w-auto"
                />
              </Link>
              <Link 
                to="/" 
                className="hidden sm:flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('accountSetup.backToHome')}
              </Link>
            </div>

            <div className="flex items-center gap-1 border border-white/20 p-1 shrink-0">
              {languages.map((lang) => (
                <button
                  key={lang}
                  onClick={() => changeLanguage(lang.toLowerCase())}
                  className={`px-2 sm:px-3 py-1.5 text-xs font-bold tracking-wider transition-all ${
                    language === lang.toLowerCase()
                      ? 'bg-[#7AC142] text-black'
                      : 'text-white/70 hover:text-white'
                  }`}
                  data-testid={`lang-${lang.toLowerCase()}`}
                >
                  {lang}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Banner */}
      <div className="bg-black py-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
              {t('accountSetup.pageTitle').split(' ')[0]}{' '}
              <span className="text-[#7AC142]">{t('accountSetup.pageTitle').split(' ').slice(1).join(' ')}</span>
            </h1>
            <p className="mt-4 text-gray-400 max-w-2xl mx-auto">
              {t('accountSetup.subtitle')}
            </p>
            <p className="mt-2 text-[#7AC142] text-sm">
              {t('accountSetup.instruction')}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Form */}
      <div className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Navigation */}
          <div ref={formTopRef} className="flex flex-wrap justify-center gap-2 mb-10 scroll-mt-24">
            {sections.map((section, index) => (
              <button
                key={section.id}
                type="button"
                onClick={() => goToSection(index)}
                aria-current={activeSection === index ? 'step' : undefined}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-bold uppercase tracking-wider transition-all ${
                  activeSection === index
                    ? 'bg-[#7AC142] text-black'
                    : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white border border-white/10'
                }`}
                data-testid={`section-tab-${section.id}`}
              >
                <section.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{section.label}</span>
                <span className="sm:hidden">{index + 1}</span>
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {/* Client Information Section */}
            {activeSection === 0 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white/5 border border-white/10 p-6 md:p-8"
              >
                <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-[#7AC142]" />
                  {t('accountSetup.sections.clientInfo')}
                </h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <InputField
                    label={t('accountSetup.clientInfo.customerName')}
                    {...fieldProps('customerName')}
                  />
                  <InputField
                    label={t('accountSetup.clientInfo.companyName')}
                    {...fieldProps('companyName')}
                  />
                  <InputField
                    label={t('accountSetup.clientInfo.taxId')}
                    {...fieldProps('taxId')}
                  />
                  <InputField
                    label={t('accountSetup.clientInfo.phoneNumber')}
                    {...fieldProps('phoneNumber')}
                    type="tel"
                  />
                  <div className="md:col-span-2">
                    <InputField
                      label={t('accountSetup.clientInfo.address')}
                      {...fieldProps('address')}
                    />
                  </div>
                  <InputField
                    label={t('accountSetup.clientInfo.district')}
                    {...fieldProps('district')}
                  />
                  <InputField
                    label={t('accountSetup.clientInfo.city')}
                    {...fieldProps('city')}
                  />
                  <InputField
                    label={t('accountSetup.clientInfo.state')}
                    {...fieldProps('state')}
                  />
                  <InputField
                    label={t('accountSetup.clientInfo.zipCode')}
                    {...fieldProps('zipCode')}
                  />
                  <div className="md:col-span-2">
                    <InputField
                      label={t('accountSetup.clientInfo.webpage')}
                      {...fieldProps('webpage')}
                      type="url"
                      placeholder="https://"
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Bank Information Section */}
            {activeSection === 1 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                {/* Primary Bank */}
                <div className="bg-white/5 border border-white/10 p-6 md:p-8">
                  <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
                    <CreditCard className="w-5 h-5 text-[#7AC142]" />
                    {t('accountSetup.bankInfo.title')}
                  </h2>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <InputField
                      label={t('accountSetup.bankInfo.beneficiaryName')}
                      {...fieldProps('beneficiaryName')}
                    />
                    <InputField
                      label={t('accountSetup.bankInfo.bankName')}
                      {...fieldProps('bankName')}
                    />
                    <InputField
                      label={t('accountSetup.bankInfo.accountNumber')}
                      {...fieldProps('accountNumber')}
                    />
                    <InputField
                      label={t('accountSetup.bankInfo.swiftCode')}
                      {...fieldProps('swiftCode')}
                    />
                    <div className="md:col-span-2">
                      <InputField
                        label={t('accountSetup.bankInfo.bankAddress')}
                        {...fieldProps('bankAddress')}
                      />
                    </div>
                    <SelectField
                      label={t('accountSetup.bankInfo.country')}
                      {...fieldProps('bankCountry')}
                      options={countryOptions}
                      placeholder={t('accountSetup.form.selectPlaceholder')}
                    />
                    <InputField
                      label={t('accountSetup.bankInfo.currency')}
                      {...fieldProps('currency')}
                      placeholder="USD, EUR, etc."
                    />
                    <InputField
                      label={t('accountSetup.bankInfo.abaCode')}
                      {...fieldProps('abaCode')}
                    />
                  </div>
                </div>

                {/* Correspondent Bank */}
                <div className="bg-white/5 border border-white/10 p-6 md:p-8">
                  <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-3">
                    <Globe2 className="w-5 h-5 text-[#7AC142]" />
                    {t('accountSetup.bankInfo.correspondentTitle')}
                  </h3>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <InputField
                      label={t('accountSetup.bankInfo.bankName')}
                      {...fieldProps('corrBankName')}
                    />
                    <InputField
                      label={t('accountSetup.bankInfo.accountNumber')}
                      {...fieldProps('corrAccountNumber')}
                    />
                    <InputField
                      label={t('accountSetup.bankInfo.swiftCode')}
                      {...fieldProps('corrSwiftCode')}
                    />
                    <InputField
                      label={t('accountSetup.bankInfo.abaCode')}
                      {...fieldProps('corrAbaCode')}
                    />
                    <div className="md:col-span-2">
                      <InputField
                        label={t('accountSetup.bankInfo.bankAddress')}
                        {...fieldProps('corrBankAddress')}
                      />
                    </div>
                    <SelectField
                      label={t('accountSetup.bankInfo.country')}
                      {...fieldProps('corrCountry')}
                      options={countryOptions}
                      placeholder={t('accountSetup.form.selectPlaceholder')}
                    />
                    <InputField
                      label={t('accountSetup.bankInfo.currency')}
                      {...fieldProps('corrCurrency')}
                    />
                  </div>
                </div>

                {/* Payoneer Option */}
                <div className="bg-white/5 border border-white/10 p-6 md:p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        name="usePayoneer"
                        checked={formData.usePayoneer}
                        onChange={handleChange}
                        className="w-5 h-5 accent-[#7AC142]"
                      />
                      <span className="text-lg font-bold text-white group-hover:text-[#7AC142] transition-colors">
                        {t('accountSetup.bankInfo.payoneerOption')}
                      </span>
                    </label>
                  </div>
                  
                  {formData.usePayoneer && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="grid md:grid-cols-2 gap-6"
                    >
                      <InputField
                        label={t('accountSetup.bankInfo.payoneerAccount')}
                        {...fieldProps('payoneerAccount')}
                        type="email"
                      />
                      <InputField
                        label={t('accountSetup.bankInfo.currencyPreference')}
                        {...fieldProps('payoneerCurrency')}
                        placeholder="USD, EUR, etc."
                      />
                    </motion.div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Contacts Section */}
            {activeSection === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                {/* Commercial Contact */}
                <div className="bg-white/5 border border-white/10 p-6 md:p-8">
                  <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-3">
                    <Users className="w-5 h-5 text-[#7AC142]" />
                    {t('accountSetup.contacts.commercial')}
                  </h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <InputField
                      label={t('accountSetup.contacts.contactName')}
                      {...fieldProps('commercialName')}
                    />
                    <InputField
                      label={t('accountSetup.contacts.position')}
                      {...fieldProps('commercialPosition')}
                    />
                    <InputField
                      label={t('accountSetup.contacts.phone')}
                      {...fieldProps('commercialPhone')}
                      type="tel"
                    />
                    <InputField
                      label={t('accountSetup.contacts.email')}
                      {...fieldProps('commercialEmail')}
                      type="email"
                    />
                    <InputField
                      label={t('accountSetup.contacts.wechat')}
                      {...fieldProps('commercialWechat')}
                    />
                    <InputField
                      label={t('accountSetup.contacts.skype')}
                      {...fieldProps('commercialSkype')}
                    />
                  </div>
                </div>

                {/* Operations Contact */}
                <div className="bg-white/5 border border-white/10 p-6 md:p-8">
                  <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-3">
                    <Users className="w-5 h-5 text-[#7AC142]" />
                    {t('accountSetup.contacts.operations')}
                  </h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <InputField
                      label={t('accountSetup.contacts.contactName')}
                      {...fieldProps('operationsName')}
                    />
                    <InputField
                      label={t('accountSetup.contacts.position')}
                      {...fieldProps('operationsPosition')}
                    />
                    <InputField
                      label={t('accountSetup.contacts.phone')}
                      {...fieldProps('operationsPhone')}
                      type="tel"
                    />
                    <InputField
                      label={t('accountSetup.contacts.email')}
                      {...fieldProps('operationsEmail')}
                      type="email"
                    />
                    <InputField
                      label={t('accountSetup.contacts.wechat')}
                      {...fieldProps('operationsWechat')}
                    />
                    <InputField
                      label={t('accountSetup.contacts.skype')}
                      {...fieldProps('operationsSkype')}
                    />
                  </div>
                </div>

                {/* Financial Contact */}
                <div className="bg-white/5 border border-white/10 p-6 md:p-8">
                  <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-3">
                    <Users className="w-5 h-5 text-[#7AC142]" />
                    {t('accountSetup.contacts.financial')}
                  </h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <InputField
                      label={t('accountSetup.contacts.contactName')}
                      {...fieldProps('financialName')}
                    />
                    <InputField
                      label={t('accountSetup.contacts.position')}
                      {...fieldProps('financialPosition')}
                    />
                    <InputField
                      label={t('accountSetup.contacts.phone')}
                      {...fieldProps('financialPhone')}
                      type="tel"
                    />
                    <InputField
                      label={t('accountSetup.contacts.cellPhone')}
                      {...fieldProps('financialCellPhone')}
                      type="tel"
                    />
                    <InputField
                      label={t('accountSetup.contacts.email')}
                      {...fieldProps('financialEmail')}
                      type="email"
                    />
                    <InputField
                      label={t('accountSetup.contacts.wechat')}
                      {...fieldProps('financialWechat')}
                    />
                    <InputField
                      label={t('accountSetup.contacts.skype')}
                      {...fieldProps('financialSkype')}
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Shipping Addresses Section */}
            {activeSection === 3 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                {formData.shippingAddresses.map((address, index) => (
                  <div key={index} className="bg-white/5 border border-white/10 p-6 md:p-8">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-bold text-white flex items-center gap-3">
                        <MapPin className="w-5 h-5 text-[#7AC142]" />
                        {t('accountSetup.shipping.warehouseTitle')} {String.fromCharCode(65 + index)}
                      </h3>
                      {formData.shippingAddresses.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeShippingAddress(index)}
                          className="flex items-center gap-2 text-red-400 hover:text-red-300 text-sm transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                          {t('accountSetup.shipping.removeAddress')}
                        </button>
                      )}
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <InputField
                        label={t('accountSetup.shipping.warehouseLegalName')}
                        {...shippingFieldProps(index, 'warehouseLegalName')}
                      />
                      <InputField
                        label={t('accountSetup.shipping.warehouseManager')}
                        {...shippingFieldProps(index, 'warehouseManager')}
                      />
                      <InputField
                        label={t('accountSetup.shipping.warehousePhone')}
                        {...shippingFieldProps(index, 'warehousePhone')}
                        type="tel"
                      />
                      <SelectField
                        label={t('accountSetup.shipping.country')}
                        {...shippingFieldProps(index, 'country')}
                        options={countryOptions}
                        placeholder={t('accountSetup.form.selectPlaceholder')}
                      />
                      <div className="md:col-span-2">
                        <InputField
                          label={t('accountSetup.shipping.shippingAddress')}
                          {...shippingFieldProps(index, 'shippingAddress')}
                        />
                      </div>
                      <InputField
                        label={t('accountSetup.shipping.city')}
                        {...shippingFieldProps(index, 'city')}
                      />
                      <InputField
                        label={t('accountSetup.shipping.state')}
                        {...shippingFieldProps(index, 'state')}
                      />
                      <InputField
                        label={t('accountSetup.shipping.zipCode')}
                        {...shippingFieldProps(index, 'zipCode')}
                      />
                    </div>
                  </div>
                ))}

                {formData.shippingAddresses.length < 3 && (
                  <button
                    type="button"
                    onClick={addShippingAddress}
                    className="w-full py-4 border-2 border-dashed border-white/20 text-white/70 font-bold uppercase tracking-wider hover:border-[#7AC142] hover:text-[#7AC142] transition-all flex items-center justify-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    {t('accountSetup.shipping.addAddress')}
                  </button>
                )}
              </motion.div>
            )}

            {/* Navigation & Submit: back on the left, the way forward on the right */}
            <div className="mt-8 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-4">
              {activeSection > 0 ? (
                <button
                  type="button"
                  onClick={() => goToSection(activeSection - 1)}
                  className={`${FORM_BUTTON} border-2 border-white text-white hover:bg-white hover:text-black`}
                  data-testid="previous-btn"
                >
                  {t('accountSetup.form.previous')}
                </button>
              ) : (
                <span className="hidden sm:block" />
              )}

              {activeSection < sections.length - 1 ? (
                <button
                  type="button"
                  onClick={() => goToSection(activeSection + 1)}
                  className={`${FORM_BUTTON} bg-[#7AC142] border-2 border-[#7AC142] text-black hover:bg-[#69A835] hover:border-[#69A835]`}
                  data-testid="next-btn"
                >
                  {t('accountSetup.form.next')}
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`${FORM_BUTTON} bg-[#7AC142] border-2 border-[#7AC142] text-black hover:bg-[#69A835] hover:border-[#69A835] disabled:opacity-50 disabled:cursor-not-allowed`}
                  data-testid="submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t('accountSetup.form.submitting')}
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      {t('accountSetup.form.submit')}
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Submit Status */}
            {submitStatus && !(submitStatus === 'validation' && Object.keys(fieldErrors).length === 0) && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`mt-6 p-4 flex items-center gap-3 ${
                  submitStatus === 'success'
                    ? 'bg-green-500/10 border border-green-500/30 text-green-400'
                    : submitStatus === 'validation'
                    ? 'bg-yellow-500/10 border border-yellow-500/30 text-yellow-400'
                    : 'bg-red-500/10 border border-red-500/30 text-red-400'
                }`}
              >
                {submitStatus === 'success' ? (
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                )}
                <span>{t(`accountSetup.form.${submitStatus}`)}</span>
              </motion.div>
            )}
          </form>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black py-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img
              src="/logo.png"
              alt="MailAmericas"
              className="h-8 w-auto"
            />
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} MailAmericas. {t('footer.rights')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AccountSetupPage;
